import {
  Avatar,
  ButtonsMenu,
  Drawer,
  IconButton,
  Radio,
  Typography,
} from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon";
import RolePermissions from "../roles/RolePermissions";
import { useState } from "react";
import DeleteRoleModal from "./DeleteRoleModal";
import EditRoleModel from "./EditRoleModel";
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";
import { TrioRole } from "@/hooks/query/acl/useGetRoles";
import { useGetRolesEmployeeCount } from "@/hooks/query/acl/useGetRolesEmployeeCount";

type TProps = {
  role: TrioRole;
  onClick?: (role: TrioRole) => void;
  selectedRoleId?: string;
  isRadio?: boolean;
};

export function getRoleCardColor(str: string, opacity: number = 1): string {
  function hexToRgba(hex: string, opacity: string): string {
    // Remove '#' if present
    hex = hex.replace(/^#/, "");
    // Parse r, g, b
    const bigint = parseInt(hex, 16);
    let r: number, g: number, b: number;
    if (hex.length === 6) {
      r = (bigint >> 16) & 255;
      g = (bigint >> 8) & 255;
      b = bigint & 255;
    } else if (hex.length === 3) {
      r = ((bigint >> 8) & 15) * 17;
      g = ((bigint >> 4) & 15) * 17;
      b = (bigint & 15) * 17;
    } else {
      // fallback to black
      r = g = b = 0;
    }
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  const roleCardColors = [
    "#DBEAFE",
    "#D1FAE5",
    "#EDE9FE",
    "#FEF3C7",
    "#FFE4E6",
    "#CFFAFE",
    "#FCE7F3",
    "#ECFCCB",
    "#E2E8F0",
    "#E0E7FF",
  ];
  if (!str || typeof str !== "string") return roleCardColors[0];
  const firstChar = str.trim().charAt(0).toUpperCase();
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const perColor = Math.ceil(letters.length / roleCardColors.length);
  const idx = letters.indexOf(firstChar);
  if (idx === -1) return roleCardColors[0];
  const colorIdx = Math.min(
    Math.floor(idx / perColor),
    roleCardColors.length - 1,
  );
  return !opacity || opacity === 1
    ? roleCardColors[colorIdx]
    : hexToRgba(roleCardColors[colorIdx], opacity.toString());
}

function RoleCard({ role, onClick, selectedRoleId, isRadio }: TProps) {
  const [isRolePermissionDrawerOpen, setIsRolePermissionDrawerOpen] =
    useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const EmployeeCount = useGetRolesEmployeeCount();

  return (
    <>
      {/* role card  */}
      <div
        className={`flex aspect-[2.3] items-start flex-col justify-between w-full active:border-gray-300 cursor-pointer border border-gray-100 p-4 rounded-[8px]`}
        style={{
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor =
            getRoleCardColor(role.name, 0.5);
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "";
        }}
        onClick={() =>
          onClick ? onClick(role) : setIsRolePermissionDrawerOpen(true)
        }
      >
        <div className="flex flex-col gap-y-4 w-full">
          <div className="flex gap-3 w-full items-start">
            <Avatar
              name={role.name}
              displayedWordAmount={2}
              fontFamily="Title4-Regular"
              size="lg"
              style={{
                backgroundColor: getRoleCardColor(role.name),
              }}
            />
            <div className="flex flex-col gap-1">
              <Typography variant="Title3-Bold">{role.name}</Typography>
              {/* <Typography color="text-gray-400" variant="Caption1-Regular">
                {role.companyId ? "Customize template" : "Trio templates"}
              </Typography> */}
            </div>
            {/* role card action  */}
            <div className="flex-1 flex" />
            {isRadio ? (
              <Radio
                onChange={() => onClick?.(role)}
                checked={role.id === selectedRoleId}
                id={role.id}
                value={role.id}
              />
            ) : (
              <ButtonsMenu
                className="w-[185px] px-2 py-1"
                lists={[
                  {
                    id: "view",
                    title: (
                      <div className="flex items-center gap-x-2">
                        <SvgIcon
                          iconName="eye"
                          className="!fill-gray-600 w-[18px] h-[18px]"
                        />
                        <Typography
                          variant="Body2-Regular"
                          color="text-gray-800"
                        >
                          View details
                        </Typography>
                      </div>
                    ),
                    onClick() {
                      setIsRolePermissionDrawerOpen(true);
                    },
                  },
                  // {
                  //   id: "managePeople",
                  //   isAccessible:
                  //     Number(role.employeesCount) !== 0 &&
                  //     role.name !== "Owner",
                  //   title: (
                  //     <div className="flex items-center gap-x-2">
                  //       <SvgIcon
                  //         iconName="usersGroupRounded"
                  //         className="!fill-gray-600 w-[18px] h-[18px]"
                  //       />
                  //       <Typography
                  //         variant="Body2-Regular"
                  //         color="text-gray-800"
                  //       >
                  //         View people
                  //       </Typography>
                  //     </div>
                  //   ),
                  //   onClick() {
                  //     setIssManagePeopleDrawerOpen(true);
                  //   },
                  // },
                  {
                    id: "managePeople",
                    isAccessible: !!role.companyId,
                    title: (
                      <div className="flex items-center gap-x-2">
                        <SvgIcon
                          iconName="documentAdd"
                          className="!fill-gray-600 w-[18px] h-[18px]"
                        />
                        <Typography
                          variant="Body2-Regular"
                          color="text-gray-800"
                        >
                          edit info
                        </Typography>
                      </div>
                    ),
                    onClick() {
                      setIsEditModalOpen(true);
                    },
                  },
                  {
                    id: "managePeople",
                    isAccessible: !!role.companyId,
                    title: (
                      <div className="flex items-center gap-x-2">
                        <SvgIcon
                          iconName="trash"
                          className="!fill-red-600 w-[18px] h-[18px]"
                        />
                        <Typography
                          variant="Body2-Regular"
                          color="text-red-600"
                        >
                          Delete
                        </Typography>
                      </div>
                    ),
                    onClick() {
                      setIsDeleteModalOpen(true);
                    },
                  },
                ]}
                button={
                  <IconButton
                    color="gray"
                    iconName="dotsVerticalRounded"
                    variant="text"
                    size="sm"
                    placeholder=""
                    svgClassName="w-5 h-5"
                  />
                }
              />
            )}
          </div>

          {/* role card description  */}
          <Typography
            color="text-gray-700"
            variant="Body2-Regular"
            className="break-all"
          >
            {role.description && role.description.length > 150
              ? `${role.description.slice(0, 150)}...`
              : role.description}
          </Typography>
        </div>

        {/* role card employee count  */}
      </div>

      {/* permission drawer */}
      <Drawer
        title={
          <div className="flex justify-center items-center gap-1">
            <span>Details</span>
            <Popover placement="bottom-start">
              <PopoverHandler>
                <div>
                  <SvgIcon
                    className="p-0 h-3 w-3 !fill-gray-400 cursor-pointer"
                    iconName="info"
                  />
                </div>
              </PopoverHandler>
              <PopoverContent
                placeholder=""
                className="dark:bg-blue-gray-800 border-0 !z-[9999] "
              >
                <div className="w-[350px] flex flex-col gap-1">
                  <Typography
                    className="leading-[24px]"
                    color="text-gray-600"
                    variant="Body2-Regular"
                  >
                    Customize and control role-based access permissions across
                    departments, profiles, employees, and software platforms.
                  </Typography>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        onClose={() => setIsRolePermissionDrawerOpen(false)}
        open={isRolePermissionDrawerOpen}
      >
        <RolePermissions
          EmployeeCount={EmployeeCount}
          selectedRole={role}
          onClose={() => setIsRolePermissionDrawerOpen(false)}
        />
      </Drawer>

      {/* View people */}

      {/* delete modal  */}
      <DeleteRoleModal
        handleClose={() => setIsDeleteModalOpen(false)}
        isOpen={isDeleteModalOpen}
        roleName={role.name}
      />

      {/* edit modal  */}
      <EditRoleModel
        handleClose={() => setIsEditModalOpen(false)}
        isOpen={isEditModalOpen}
        role={role}
      />
    </>
  );
}

export default RoleCard;
